/* House of Hannah — global behaviours
   Drawers, sticky header, carousels, wishlist, quantity, reveal animations. */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Drawers (cart / mobile menu / search)
     ------------------------------------------------------------------ */
  const drawers = {
    open(name) {
      const drawer = document.querySelector(`[data-drawer="${name}"]`);
      if (!drawer) return;
      this.closeAll();
      drawer.classList.add('is-open');
      document.body.classList.add('drawer-open');
      drawer.focus({ preventScroll: true });
      this._active = drawer;
    },
    closeAll() {
      document.querySelectorAll('.drawer.is-open').forEach((d) => d.classList.remove('is-open'));
      document.body.classList.remove('drawer-open');
      this._active = null;
    },
  };
  window.hohDrawers = drawers;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      drawers.open(opener.getAttribute('data-drawer-open'));
      return;
    }
    if (event.target.closest('[data-drawer-close]') || event.target.closest('[data-drawer-overlay]')) {
      drawers.closeAll();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') drawers.closeAll();
  });

  /* ------------------------------------------------------------------
     Sticky header — soft blur + shadow once scrolled
     ------------------------------------------------------------------ */
  const header = document.querySelector('.site-header');
  if (header) {
    const canHide = header.dataset.sticky !== 'false';
    // The header's Shopify section wrapper — we reserve its height while the
    // header is pinned so the page content doesn't jump.
    const wrapper = header.parentElement;
    const REVEAL_DELTA = 4; // ignore tiny scroll jitters
    let lastY = window.scrollY;
    let ticking = false;

    const wrapperTop = () =>
      wrapper.getBoundingClientRect().top + window.scrollY;

    const pin = () => {
      if (header.classList.contains('is-pinned')) return;
      wrapper.style.height = header.offsetHeight + 'px'; // reserve space
      header.classList.add('is-pinned', 'is-hidden'); // start hidden, reveal on scroll up
    };
    const unpin = () => {
      header.classList.remove('is-pinned', 'is-hidden');
      wrapper.style.height = '';
    };

    const update = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 24);

      if (canHide) {
        const top = wrapperTop();
        const trigger = top + header.offsetHeight;
        const pinned = header.classList.contains('is-pinned');

        if (!pinned && y > trigger) {
          pin();
        } else if (pinned && y <= top) {
          unpin();
        } else if (pinned) {
          const delta = y - lastY;
          if (Math.abs(delta) > REVEAL_DELTA) {
            if (delta > 0) header.classList.add('is-hidden'); // down → hide
            else header.classList.remove('is-hidden'); // up → reveal
          }
        }
      }
      lastY = y;
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  /* ------------------------------------------------------------------
     Scroll reveal
     ------------------------------------------------------------------ */
  const revealItems = document.querySelectorAll('.reveal');
  if (revealItems.length && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    revealItems.forEach((item) => observer.observe(item));
  } else {
    revealItems.forEach((item) => item.classList.add('is-visible'));
  }

  /* ------------------------------------------------------------------
     Horizontal carousels — prev/next buttons scroll one "page"
     ------------------------------------------------------------------ */
  document.querySelectorAll('[data-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-carousel-track]');
    if (!track) return;
    // Controls may sit in the section header, outside [data-carousel],
    // so look for them within the whole section.
    const scope = carousel.closest('.section') || carousel;
    const prev = scope.querySelector('[data-carousel-prev]');
    const next = scope.querySelector('[data-carousel-next]');

    const step = () => {
      const item = track.querySelector(':scope > *');
      if (!item) return track.clientWidth * 0.8;
      const gap = parseFloat(getComputedStyle(track).columnGap || 0);
      return item.getBoundingClientRect().width + gap;
    };

    const updateState = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.toggleAttribute('disabled', track.scrollLeft <= 2);
      if (next) next.toggleAttribute('disabled', track.scrollLeft >= max);
    };

    prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', updateState, { passive: true });
    window.addEventListener('resize', updateState);
    updateState();
  });

  /* ------------------------------------------------------------------
     Custom sort dropdown (collection / search)
     ------------------------------------------------------------------ */
  document.querySelectorAll('[data-sort-dropdown]').forEach((dd) => {
    const toggle = dd.querySelector('[data-sort-toggle]');
    const panel = dd.querySelector('[data-sort-panel]');
    const current = dd.querySelector('[data-sort-current]');
    const select = dd.querySelector('[data-facet-sort]');
    if (!toggle || !panel || !select) return;

    const open = () => {
      panel.hidden = false;
      dd.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    };
    const close = () => {
      panel.hidden = true;
      dd.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    };

    toggle.addEventListener('click', (event) => {
      event.stopPropagation();
      if (panel.hidden) open();
      else close();
    });

    const choose = (opt) => {
      const value = opt.getAttribute('data-sort-value');
      select.value = value;
      if (current) current.textContent = opt.textContent.trim();
      panel.querySelectorAll('[data-sort-value]').forEach((o) => {
        const isSel = o === opt;
        o.classList.toggle('is-selected', isSel);
        o.setAttribute('aria-selected', isSel ? 'true' : 'false');
      });
      close();
      select.dispatchEvent(new Event('change', { bubbles: true }));
    };

    panel.querySelectorAll('[data-sort-value]').forEach((opt) => {
      opt.addEventListener('click', () => choose(opt));
      opt.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          choose(opt);
        }
      });
    });

    document.addEventListener('click', (event) => {
      if (!dd.contains(event.target)) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
  });

  /* ------------------------------------------------------------------
     Quantity selectors (product + cart page)
     ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-change]');
    if (!button) return;
    const wrapper = button.closest('.quantity');
    const input = wrapper && wrapper.querySelector('input[type="number"]');
    if (!input) return;
    const delta = parseInt(button.getAttribute('data-quantity-change'), 10);
    const min = parseInt(input.min || '1', 10);
    const next = Math.max(min, (parseInt(input.value, 10) || min) + delta);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------
     Wishlist — localStorage backed
     ------------------------------------------------------------------ */
  const WISHLIST_KEY = 'hoh:wishlist';

  const wishlist = {
    read() {
      try {
        return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
      } catch (e) {
        return [];
      }
    },
    write(items) {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
      document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { items } }));
    },
    toggle(handle) {
      const items = this.read();
      const index = items.indexOf(handle);
      if (index === -1) items.push(handle);
      else items.splice(index, 1);
      this.write(items);
      return index === -1;
    },
    has(handle) {
      return this.read().indexOf(handle) !== -1;
    },
  };
  window.hohWishlist = wishlist;

  const syncWishlistButtons = () => {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const active = wishlist.has(button.getAttribute('data-handle'));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    const badge = document.querySelector('[data-wishlist-count]');
    if (badge) {
      const count = wishlist.read().length;
      badge.textContent = count > 0 ? count : '';
      badge.classList.toggle('is-empty', count === 0);
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    wishlist.toggle(button.getAttribute('data-handle'));
  });

  document.addEventListener('wishlist:changed', syncWishlistButtons);
  syncWishlistButtons();

  /* ------------------------------------------------------------------
     Recently viewed — store handles on product pages
     ------------------------------------------------------------------ */
  const RECENT_KEY = 'hoh:recently-viewed';
  window.hohRecentlyViewed = {
    read() {
      try {
        return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
      } catch (e) {
        return [];
      }
    },
    add(handle) {
      const items = this.read().filter((h) => h !== handle);
      items.unshift(handle);
      localStorage.setItem(RECENT_KEY, JSON.stringify(items.slice(0, 12)));
    },
  };

  /* ------------------------------------------------------------------
     Newsletter / contact success focus
     ------------------------------------------------------------------ */
  const successMessage = document.querySelector('.form__message--success[data-autofocus]');
  if (successMessage) {
    successMessage.scrollIntoView({ block: 'center' });
  }
})();
